import styled from '@emotion/styled';
import React, { useState } from 'react';
import { getBrandLogo, i18n, formatPrice, resolvePremiums, useCalculationInput, PropertyBrandWithProducts } from 'src/common';
import { colors } from 'src/theme';
import { CardWithTitle } from './cardWithTitle';
import { BrandLogo, CoveragesAccordion, LoaderSquare } from '../other';
import { Flex } from '../grid';
import { DownloadIcon, StarIcon } from '../icons';
import { Text } from '../typography';
import { ToggleButtonGroup } from '../inputs';
import { Button } from '../buttons';

interface OfferPreviewCardProps {
  brandCalc: PropertyBrandWithProducts;
  toggledProductCode: string | null;
  onTogglePackage: (packageName: string) => void;
  onDownloadTerms?: () => void;
  calculationsLoading?: boolean;
}

const ToggleWrapper = styled.div`
  width: 100%;
`;

export const OfferPreviewCard: React.FC<OfferPreviewCardProps> = ({
  brandCalc,
  onTogglePackage,
  toggledProductCode,
  onDownloadTerms,
  calculationsLoading,
}) => {
  const initialProduct = brandCalc.products.find(pack => toggledProductCode === pack.code);
  const [selectedProductCode, setSelectedProductCode] = useState<string>(initialProduct?.code ?? brandCalc.products[0].code);
  const { setAdditionalCoverages, additionalCoverages } = useCalculationInput();

  const selectedProduct = brandCalc.products.find(prd => selectedProductCode === prd.code) || brandCalc?.products?.[0];

  const handleTogglePackage = (productCode: string) => {
    const toggled = brandCalc.products.find(pkg => pkg.code === productCode);
    if (!toggled) return;
    setSelectedProductCode(productCode);
    onTogglePackage(productCode);
  };

  const handleToggleAdditionalCoverage = (coverageCode: string) => {
    setAdditionalCoverages(brandCalc.code, coverageCode);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredAdditionalCoverages = selectedProduct?.calculation?.additionalCoverages.filter((pAC) =>
    !additionalCoverages.find(aC => aC.coverageCode === selectedProduct.productTypeSpecific.coverages?.find((cov) => cov.disables === pAC.code)?.code)
  )
  const coverages = [selectedProduct?.calculation?.coverages.map(cov => ({ ...cov, isIncluded: true })), filteredAdditionalCoverages]
    .flat().filter(cov => cov !== undefined);

  const selectedCalculation = selectedProduct.calculation;

  if (!selectedCalculation || !selectedProduct) return;

  return (
    <CardWithTitle padding="8px 8px">
      <Flex vertical gap={24} style={{ width: '100%', padding: 0 }}>
        <Flex gap={4} justify="space-between" style={{ padding: 4 }}>
          <Flex vertical justify='flex-end' align="flex-start">
            <BrandLogo brandCode={brandCalc.code} src={getBrandLogo(brandCalc.code)} alt={brandCalc.code} />
            <Flex gap={4}>
              <Text type="base">{i18n.t('components.cards.offerPreviewCard.package')}</Text>
              <Text type="base" bold={true}>{selectedCalculation.packageName}</Text>
            </Flex>
          </Flex>
          { calculationsLoading ? (
            <Flex style={{ paddingRight: 16 }}>
              <LoaderSquare />
            </Flex>
          ) : (
            <Flex vertical justify="center" align="flex-end">
              <Text type="small">{formatPrice(resolvePremiums(selectedCalculation).monthly)}/mj</Text>
              <Text type="base" bold={true}>{formatPrice(resolvePremiums(selectedCalculation).annual)}/god</Text>
            </Flex>
          )}
        </Flex>

        {/* Upsell Banner */}
        <Flex vertical gap={8} style={{ background: colors.note3, borderRadius: '4px', padding: '8px' }}>
          <Text type="base">
            {i18n.t('components.cards.offerPreviewCard.upsellMessage')}
          </Text>
          <ToggleWrapper>
            <ToggleButtonGroup
              options={brandCalc.products.map((pkg, i) => ({
                value: pkg.code,
                label: pkg.calculation!.packageName,
                icon: i === brandCalc.products.length - 1 ? <StarIcon color={selectedProduct.code !== pkg.code ? "#F4901F" : undefined}  height={16} width={16} /> : undefined
              }))}
              value={selectedProduct.code}
              onChange={handleTogglePackage}
            />
          </ToggleWrapper>
          <Text type="base">
            {i18n.t('components.cards.offerPreviewCard.upsellDescription')}
          </Text>
        </Flex>

        { coverages?.length > 0 && (
          <CoveragesAccordion
            coverageGroups={coverages}
            handleToggleCalc={handleToggleAdditionalCoverage}
            selectedAdditionalCoverages={additionalCoverages}
            alwaysOpen={true}
          />
        )}

        <Flex align="center" gap={8} style={{ padding: "0 8px" }}>
          <DownloadIcon/>
          <Button btnType="textPrimary" size="small" onClick={onDownloadTerms}>
            {i18n.t('components.cards.offerPreviewCard.insuranceTerms')}
          </Button>
        </Flex>
      </Flex>
    </CardWithTitle>
  );
};
